import { useRef, useState, type MouseEvent } from 'react';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  MoreHorizontalIcon,
  PlusIcon,
  TrashIcon,
} from 'lucide-react';
import type {
  NumberingStyle,
  NumberingStylePosition,
} from '@worldathletics/types';

import { Dropdown } from '@/components/atoms/dropdown';
import { useClickOutside } from '@/hooks/use-click-outside';
import {
  NUMBERING_STYLE_OPTIONS,
  NUMBERING_STYLE_POSITION_OPTIONS,
} from '@/lib/consts';

import './block-menu.scss';

type BlockMenuProps = {
  variant: 'container' | 'leaf';
  showNumbering?: boolean;
  numberingStyle?: NumberingStyle;
  onNumberingStyleChange?: (next: NumberingStyle) => void;
  numberingStylePosition?: NumberingStylePosition;
  onNumberingStylePositionChange?: (next: NumberingStylePosition) => void;
  propagateNumbering?: boolean;
  onPropagateNumberingChange?: (next: boolean) => void;
  continueNumbering?: boolean;
  onContinueNumberingChange?: (next: boolean) => void;
  transparent?: boolean;
  onTransparentChange?: (next: boolean) => void;
  showTransparentToggle?: boolean;
  onDelete: () => void;
  onAddBlock?: () => void;
  onAddAbove?: () => void;
  onAddBelow?: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  onOpenChange?: (open: boolean) => void;
};

function BlockMenu({
  variant,
  showNumbering: showNumberingProp,
  numberingStyle = 'free',
  onNumberingStyleChange,
  numberingStylePosition,
  onNumberingStylePositionChange,
  propagateNumbering = false,
  onPropagateNumberingChange,
  continueNumbering = false,
  onContinueNumberingChange,
  transparent = false,
  onTransparentChange,
  showTransparentToggle = true,
  onDelete,
  onAddBlock,
  onAddAbove,
  onAddBelow,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  onOpenChange,
}: BlockMenuProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const openSelectMenuCountRef = useRef(0);

  useClickOutside(containerRef, () => {
    if (open && openSelectMenuCountRef.current === 0) {
      setOpen(false);
      onOpenChange?.(false);
    }
  });

  function handleNestedSelectMenuOpen(): void {
    openSelectMenuCountRef.current += 1;
  }

  function handleNestedSelectMenuClose(): void {
    openSelectMenuCountRef.current = Math.max(
      0,
      openSelectMenuCountRef.current - 1,
    );
  }

  function handleTrigger(e: MouseEvent<HTMLButtonElement>): void {
    e.stopPropagation();
    const nextOpen = !open;
    if (!nextOpen) {
      openSelectMenuCountRef.current = 0;
    }
    setOpen(nextOpen);
    onOpenChange?.(nextOpen);
  }

  function handleDropdownChange(option: { value: string }): void {
    onNumberingStyleChange?.(option.value as NumberingStyle);
  }

  function handlePositionDropdownChange(option: { value: string }): void {
    onNumberingStylePositionChange?.(option.value as NumberingStylePosition);
  }

  function handlePropagateToggle(): void {
    onPropagateNumberingChange?.(propagateNumbering ? false : true);
  }

  function handleContinueToggle(): void {
    onContinueNumberingChange?.(continueNumbering ? false : true);
  }

  function handleTransparentToggle(): void {
    onTransparentChange?.(transparent ? false : true);
  }

  function handleDelete(): void {
    setOpen(false);
    onOpenChange?.(false);
    onDelete();
  }

  function handleAddBlock(): void {
    setOpen(false);
    onOpenChange?.(false);
    onAddBlock?.();
  }

  function handleAddAbove(): void {
    setOpen(false);
    onOpenChange?.(false);
    onAddAbove?.();
  }

  function handleAddBelow(): void {
    setOpen(false);
    onOpenChange?.(false);
    onAddBelow?.();
  }

  function handleMoveUp(e: MouseEvent<HTMLButtonElement>): void {
    e.stopPropagation();
    onMoveUp?.();
  }

  function handleMoveDown(e: MouseEvent<HTMLButtonElement>): void {
    e.stopPropagation();
    onMoveDown?.();
  }

  const propagateChecked = propagateNumbering === false;
  const showNumbering = showNumberingProp ?? variant === 'container';
  const showAddBlock = variant === 'container' && Boolean(onAddBlock);

  const selectedNumberingStyle = NUMBERING_STYLE_OPTIONS.find(
    o => o.value === numberingStyle,
  );
  const selectedPosition = NUMBERING_STYLE_POSITION_OPTIONS.find(
    o => o.value === (numberingStylePosition ?? 'suffix'),
  );

  return (
    <div
      ref={containerRef}
      className={open ? 'block-menu block-menu--open' : 'block-menu'}
    >
      <button
        type="button"
        className="block-menu__trigger"
        onClick={handleTrigger}
        aria-label="Block actions"
        title="Block actions"
        aria-expanded={open}
      >
        <MoreHorizontalIcon size={16} />
      </button>
      {open && (
        <div className="block-menu__popover" role="menu">
          {showAddBlock && (
            <button
              type="button"
              className="block-menu__action block-menu__action--primary"
              onClick={handleAddBlock}
            >
              <PlusIcon size={14} />
              Add block
            </button>
          )}
          {onAddAbove && (
            <button
              type="button"
              className="block-menu__action"
              onClick={handleAddAbove}
            >
              <PlusIcon size={14} />
              Add block above
            </button>
          )}
          {onAddBelow && (
            <button
              type="button"
              className="block-menu__action"
              onClick={handleAddBelow}
            >
              <PlusIcon size={14} />
              Add block below
            </button>
          )}
          <hr />
          {showNumbering && (
            <>
              {/* A transparent block is skipped in numbering and promotes its
                  children to the grandparent — so its own children-style,
                  position, and propagate/continue flags are all moot. Hide
                  them and surface only the transparency toggle. */}
              {!transparent && (
                <>
                  <div className="block-menu__group">
                    <label>Numbering style (children)</label>
                    <Dropdown
                      options={NUMBERING_STYLE_OPTIONS}
                      value={selectedNumberingStyle}
                      onChange={handleDropdownChange}
                      onMenuOpen={handleNestedSelectMenuOpen}
                      onMenuClose={handleNestedSelectMenuClose}
                    />
                  </div>
                  <div className="block-menu__group">
                    <label>Numbering position (self)</label>
                    <Dropdown
                      options={NUMBERING_STYLE_POSITION_OPTIONS}
                      value={selectedPosition}
                      onChange={handlePositionDropdownChange}
                      onMenuOpen={handleNestedSelectMenuOpen}
                      onMenuClose={handleNestedSelectMenuClose}
                    />
                  </div>
                  <div
                    className="block-menu__action block-menu__action--toggle"
                    onClick={handlePropagateToggle}
                  >
                    <input
                      type="checkbox"
                      checked={propagateChecked}
                      readOnly
                      aria-label="Use custom numbering (children)"
                    />
                    Use custom numbering (children)
                  </div>
                  <div
                    className="block-menu__action block-menu__action--toggle"
                    onClick={handleContinueToggle}
                  >
                    <input
                      type="checkbox"
                      checked={continueNumbering}
                      readOnly
                      aria-label="Continue numbering"
                    />
                    Continue numbering
                  </div>
                </>
              )}
              {showTransparentToggle && (
                <div
                  className="block-menu__action block-menu__action--toggle"
                  onClick={handleTransparentToggle}
                >
                  <input
                    type="checkbox"
                    checked={transparent}
                    readOnly
                    aria-label="Exclude from numbering (transparent)"
                  />
                  Exclude from numbering (transparent)
                </div>
              )}
              <hr />
            </>
          )}
          {onMoveUp && (
            <button
              type="button"
              className="block-menu__action"
              onClick={handleMoveUp}
              disabled={!canMoveUp}
              aria-label="Move up"
              title="Move up"
              aria-disabled={!canMoveUp}
            >
              <ChevronUpIcon size={14} />
              Move up
            </button>
          )}
          {onMoveDown && (
            <button
              type="button"
              className="block-menu__action"
              onClick={handleMoveDown}
              disabled={!canMoveDown}
              aria-label="Move down"
              title="Move down"
              aria-disabled={!canMoveDown}
            >
              <ChevronDownIcon size={14} />
              Move down
            </button>
          )}
          <button
            type="button"
            className="block-menu__action block-menu__action--danger"
            onClick={handleDelete}
          >
            <TrashIcon size={14} />
            Delete block
          </button>
        </div>
      )}
    </div>
  );
}

export { BlockMenu };
